// client/src/dnd/components/pixi_parts/WeatherOverlay.js
import React, { useRef, useEffect, useCallback } from "react";
import * as PIXI from "pixi.js";
import { useMap } from "react-leaflet";
import { createEmitterRuntime } from "../../emitter_tool/runtime/RuntimeBridge.js";
import { coerceSnapshotForLegacyImport } from "../../emitter_tool/utils/PresetApply.js";
import rainPreset from "./effects/rain.js";

export default function WeatherOverlay({ imageSize, gridConfig }) {
  const map = useMap();

  const rainContainerRef = useRef(null);
  const rainAppRef = useRef(null);
  const emitterRuntimeRef = useRef(null);
  const rainActiveRef = useRef(false);

  const applyMapTransformToApp = useCallback(
    (app) => {
      if (!app || !map || !imageSize) return;
      if (!app.stage) return;
      const container = map.getContainer?.();
      if (!container) return;

      const w = container.clientWidth || 0;
      const h = container.clientHeight || 0;
      if (w <= 0 || h <= 0) return;

      app.renderer.resize(w, h);

      const imgW = Number(imageSize?.[0]) || 0;
      const imgH = Number(imageSize?.[1]) || 0;
      const p0 = map.latLngToContainerPoint([0, 0]);
      const pX = map.latLngToContainerPoint([0, imgW]);
      const pY = map.latLngToContainerPoint([imgH, 0]);
      const sx = imgW > 0 ? (pX.x - p0.x) / imgW : 1;
      const sy = imgH > 0 ? (pY.y - p0.y) / imgH : 1;

      app.stage.position.set(p0.x, p0.y);
      app.stage.scale.set(sx, sy);

      try {
        app.stage.interactive = true;
        app.stage.hitArea = new PIXI.Rectangle(0, 0, w, h);
      } catch {}
    },
    [map, imageSize]
  );

  useEffect(() => {
    let destroyed = false;

    function makeRainApp() {
      if (!rainContainerRef.current || rainAppRef.current) return;

      const app = new PIXI.Application({
        width: 0,
        height: 0,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        backgroundAlpha: 0,
        autoStart: false,
        powerPreference: "high-performance",
        clearBeforeRender: true,
      });

      const canvas = app.view;
      Object.assign(canvas.style, {
        position: "absolute",
        top: "0",
        left: "0",
        pointerEvents: "none",
        width: "100%",
        height: "100%",
        background: "transparent",
      });

      app.stage.sortableChildren = true;

      const particlesLayer = new PIXI.Container();
      particlesLayer.sortableChildren = true;
      particlesLayer.zIndex = 400;
      app.stage.addChild(particlesLayer);
      app.particlesLayer = particlesLayer;

      if (!destroyed && rainContainerRef.current) {
        rainContainerRef.current.appendChild(canvas);
        rainAppRef.current = app;

        const ppm =
          (gridConfig && (gridConfig.metre_in_pixels || gridConfig.meter_in_pixels)) || 80;

        emitterRuntimeRef.current = createEmitterRuntime({
          PIXIRef: PIXI,
          app,
          pxPerMeter: ppm,
        });

        try {
          rainContainerRef.current.style.opacity = "0";
          rainContainerRef.current.style.transition = "opacity 0.6s ease-out";
        } catch {}

        applyMapTransformToApp(app);
      } else {
        try {
          app.destroy(true);
        } catch {}
      }
    }

    makeRainApp();

    return () => {
      destroyed = true;

      const rt = emitterRuntimeRef.current;
      if (rt && typeof rt.destroy === "function") {
        try {
          rt.destroy();
        } catch {}
      }
      emitterRuntimeRef.current = null;

      const app = rainAppRef.current;
      if (app) {
        try {
          app.destroy(true);
        } catch {}
      }
      rainAppRef.current = null;
    };
  }, [gridConfig, applyMapTransformToApp]);

  useEffect(() => {
    const ppm =
      (gridConfig && (gridConfig.metre_in_pixels || gridConfig.meter_in_pixels)) || 80;

    const rt = emitterRuntimeRef.current;
    if (rt && typeof rt.setEnvironment === "function") {
      try {
        rt.setEnvironment({ pxPerMeter: ppm });
      } catch {}
    }
  }, [gridConfig]);

  useEffect(() => {
    if (!map || !imageSize) return;

    function updateAll() {
      applyMapTransformToApp(rainAppRef.current);
    }

    updateAll();
    map.on("zoom viewreset resize move", updateAll);
    return () => {
      map.off("zoom viewreset resize move", updateAll);
    };
  }, [map, imageSize, applyMapTransformToApp]);

  useEffect(() => {
    function handleToggleRain(e) {
      const on =
        e && e.detail && typeof e.detail.raining !== "undefined"
          ? !!e.detail.raining
          : !rainActiveRef.current;

      rainActiveRef.current = on;

      const app = rainAppRef.current;
      const runtime = emitterRuntimeRef.current;
      const container = rainContainerRef.current;
      if (!app || !runtime || !container) return;

      try {
        container.style.opacity = on ? "1" : "0";
      } catch {}

      if (!on) {
        if (typeof runtime.stopEmitting === "function") {
          try {
            runtime.stopEmitting();
          } catch {}
        }
        return;
      }

      applyMapTransformToApp(app);

      const imgW = Array.isArray(imageSize) ? Number(imageSize[0]) || 1920 : 1920;
      const imgH = Array.isArray(imageSize) ? Number(imageSize[1]) || 1080 : 1080;

      const rainPPM = 38.333333333333336;
      const safeImageSize = [imgW, imgH];

      const helpers = {
        imageSize: () => safeImageSize,
        pxPerMeter: rainPPM,
        looseBounds: true,
        looseBoundsMargin: imgH,
        getOrigin: ({ imageRect }) => ({
          x: imageRect.width * 0.5,
          y: imageRect.height * 0.5,
        }),
      };

      let runtimePreset = rainPreset;
      try {
        const coerced = coerceSnapshotForLegacyImport(rainPreset);
        if (coerced && typeof coerced === "object") {
          runtimePreset = coerced;
        }
      } catch {}

      runtime.runPreset(
        runtimePreset,
        null,
        app.particlesLayer || app.stage,
        app.particlesLayer || app.stage,
        helpers
      );
    }

    window.addEventListener("pointatlas-demo-toggle-rain", handleToggleRain);
    return () => {
      window.removeEventListener("pointatlas-demo-toggle-rain", handleToggleRain);
    };
  }, [imageSize, applyMapTransformToApp]);

  const drawRain = useCallback(() => {
    const app = rainAppRef.current;
    const runtime = emitterRuntimeRef.current;

    if (!app || !map) {
      requestAnimationFrame(drawRain);
      return;
    }

    if (runtime && typeof runtime.update === "function") {
      try {
        runtime.update(1 / 60);
      } catch {}
    }

    try {
      app.renderer.render(app.stage);
    } catch {}

    requestAnimationFrame(drawRain);
  }, [map]);

  useEffect(() => {
    const id = requestAnimationFrame(drawRain);
    return () => cancelAnimationFrame(id);
  }, [drawRain]);

  return (
    <div
      ref={rainContainerRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        width: "100%",
        height: "100%",
        zIndex: 500,
        backgroundColor: "transparent",
        opacity: 0,
      }}
    />
  );
}
